import { useState } from "react";
import { ChevronDown, ChevronUp, Calendar, Clock } from "lucide-react";
import { fmtWeekKey, fmtMonthKey, computeStats, CATS, getWeekKey, getMonthKey } from "@/lib/chartUtils";
import StatsView from "./StatsView";

export default function HistoryView({ events, members }) {
  const [hType, setHType] = useState("week");
  const [hKey, setHKey] = useState(null);

  const wk = getWeekKey(new Date());
  const mk = getMonthKey(new Date());
  const pastWks = [...new Set(events.map(e => e.weekKey))].sort().reverse().filter(k => k !== wk);
  const pastMks = [...new Set(events.map(e => e.monthKey))].sort().reverse().filter(k => k !== mk);

  return (
    <div data-testid="history-view">
      <h3 className="text-[10px] font-bold text-zinc-400 tracking-[0.2em] uppercase mb-4">Browse Past Periods</h3>

      <div className="flex gap-2 mb-4">
        <button
          data-testid="history-by-week"
          onClick={() => { setHType("week"); setHKey(null); }}
          className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-md text-xs font-bold border transition-all ${
            hType === "week" ? 'bg-zinc-900 text-white border-zinc-900' : 'bg-white text-zinc-600 border-zinc-200 hover:border-zinc-300'
          }`}
        >
          <Calendar className="w-3.5 h-3.5" /> By Week
        </button>
        <button
          data-testid="history-by-month"
          onClick={() => { setHType("month"); setHKey(null); }}
          className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-md text-xs font-bold border transition-all ${
            hType === "month" ? 'bg-zinc-900 text-white border-zinc-900' : 'bg-white text-zinc-600 border-zinc-200 hover:border-zinc-300'
          }`}
        >
          <Clock className="w-3.5 h-3.5" /> By Month
        </button>
      </div>

      {hType === "week" && pastWks.map(k => {
        const evs = events.filter(e => e.weekKey === k);
        const sel = hKey === k;
        const stats = computeStats(evs);
        return (
          <div key={k} className="mb-2">
            <button
              data-testid={`history-week-${k}`}
              onClick={() => setHKey(sel ? null : k)}
              className={`w-full flex items-center justify-between px-4 py-3 rounded-lg border-2 transition-all text-left ${
                sel ? 'border-orange-500 bg-orange-50/50' : 'border-zinc-100 bg-zinc-50 hover:border-zinc-200'
              }`}
            >
              <div>
                <p className="text-sm font-bold text-zinc-950">Week of {fmtWeekKey(k)}</p>
                <p className="text-xs text-zinc-400">{evs.length} events</p>
              </div>
              <div className="flex items-center gap-2">
                {CATS.slice(0, 4).map(c => {
                  const tot = Object.values(stats).reduce((s, p) => s + (p[c.key] || 0), 0);
                  return tot > 0 ? (
                    <span key={c.key} className="text-[11px] font-bold bg-zinc-100 text-zinc-600 rounded-full px-2 py-0.5">{tot}</span>
                  ) : null;
                })}
                {sel ? <ChevronUp className="w-4 h-4 text-zinc-400" /> : <ChevronDown className="w-4 h-4 text-zinc-400" />}
              </div>
            </button>
            {sel && (
              <div className="mt-1 bg-zinc-50 border border-zinc-100 rounded-lg p-4">
                <StatsView events={evs} members={members} title={`Week of ${fmtWeekKey(k)}`} />
              </div>
            )}
          </div>
        );
      })}
      {hType === "week" && pastWks.length === 0 && (
        <p className="text-center text-zinc-400 py-8 text-sm">No past weeks yet.</p>
      )}

      {hType === "month" && pastMks.map(k => {
        const evs = events.filter(e => e.monthKey === k);
        const sel = hKey === k;
        const bizTot = evs.filter(e => e.type === "biz").reduce((s, e) => s + e.amt, 0);
        return (
          <div key={k} className="mb-2">
            <button
              data-testid={`history-month-${k}`}
              onClick={() => setHKey(sel ? null : k)}
              className={`w-full flex items-center justify-between px-4 py-3 rounded-lg border-2 transition-all text-left ${
                sel ? 'border-orange-500 bg-orange-50/50' : 'border-zinc-100 bg-zinc-50 hover:border-zinc-200'
              }`}
            >
              <div>
                <p className="text-sm font-bold text-zinc-950">{fmtMonthKey(k)}</p>
                <p className="text-xs text-zinc-400">{evs.length} events · {evs.filter(e => e.type === "biz").length} deals</p>
              </div>
              <div className="flex items-center gap-2">
                {bizTot > 0 && (
                  <span className="text-[11px] font-bold bg-red-100 text-red-700 rounded-full px-2 py-0.5">${bizTot.toLocaleString()}</span>
                )}
                {sel ? <ChevronUp className="w-4 h-4 text-zinc-400" /> : <ChevronDown className="w-4 h-4 text-zinc-400" />}
              </div>
            </button>
            {sel && (
              <div className="mt-1 bg-zinc-50 border border-zinc-100 rounded-lg p-4">
                <StatsView events={evs} members={members} title={fmtMonthKey(k)} />
              </div>
            )}
          </div>
        );
      })}
      {hType === "month" && pastMks.length === 0 && (
        <p className="text-center text-zinc-400 py-8 text-sm">No past months yet.</p>
      )}
    </div>
  );
}
